import React from "react";
import Header from "../components/layout/Header";
import AdminHeader from "../components/layout/AdminHeader";
import Footer from "../components/layout/Footer";
import Pagination from "../components/ui/Pagination";
import datasets from "../constants/DataSet";
import dataViewModeConfig from "../features/data-view/config/dataViewModeConfig";
import datasetViewConfig from "../features/data-view/config/datasetViewConfig";
import {
  useDataView,
  useDataViewActions,
  useDataViewDerived,
  useDataViewPageController,
  useDataViewSelection,
  useDataViewState,
} from "../features/data-view/hooks";
import {
  buildBulkDownloadBatchOptions,
} from "../features/data-view/utils/downloadDataViewFiles";
import {
  DEFAULT_DOC_STATUS,
  normalizeDocStatusValue,
  shouldShowDocStatusFeature,
} from "../features/data-view/utils/docStatus";
import {
  DataViewHeroSection,
  DatasetRecordTable,
  RecordDetailCard,
} from "../features/data-view/components";
import { BULK_DOWNLOAD_BATCH_SIZE, PAGE_SIZE_OPTIONS, DETAIL_PANEL_POSITIONS } from "../constants/DataView.constants";

const DataViewPage = ({ mode = "research" }) => {
  const modeConfig = dataViewModeConfig[mode] || dataViewModeConfig.research;
  const isAdminMode = modeConfig.canEditRecords;
  const {
    isDetailPanelOpen,
    setIsDetailPanelOpen,
    detailPanelPosition,
    setDetailPanelPosition,
    selectedBatchValue,
    setSelectedBatchValue,
    downloadError,
    setDownloadError,
    isBulkDownloading,
    setIsBulkDownloading,
    isSavingRecord,
    setIsSavingRecord,
    saveRecordError,
    setSaveRecordError,
    isRevertingRecord,
    setIsRevertingRecord,
    revertRecordError,
    setRevertRecordError,
    companyBulkError,
    setCompanyBulkError,
    isMergingCompanies,
    setIsMergingCompanies,
    isDeletingCompanies,
    setIsDeletingCompanies,
  } = useDataViewState({
    initialDetailPanelPosition: DETAIL_PANEL_POSITIONS.RIGHT,
  });
  const {
    activeDataset,
    records,
    listError,
    listLoading,
    detailError,
    detailLoading,
    selectedRecordId,
    detailRecord,
    editableDetailRecord,
    detailAuditMetadata,
    isViewingOriginalRecord,
    searchInput,
    submittedQuery,
    selectedStatus,
    pagination,
    setSearchInput,
    setSelectedRecordId,
    submitSearch,
    clearSearch,
    handleStatusChange,
    goToPage,
    goToLastPage,
    updatePerPage,
    updateDataset,
    openLinkedRecord,
    applyRecordUpdate,
    viewOriginalRecord,
    viewCurrentRecord,
    refreshData,
    clearDetailState,
    beginDetailLoading,
  } = useDataView({ mode });
  const { selectedRowIds, toggleRow, toggleAll, setSelectedRowIds } =
    useDataViewSelection(records);

  const activeDatasetConfig = datasetViewConfig[activeDataset] || datasetViewConfig[datasets[0]?.id];
  const showDocStatusFilter = isAdminMode && shouldShowDocStatusFeature(activeDataset);
  const activeConfig = React.useMemo(() => {
    if (!isAdminMode || !shouldShowDocStatusFeature(activeDataset)) {
      return activeDatasetConfig;
    }

    const summaryColumns = activeDatasetConfig.summary?.columns || [];
    if (summaryColumns.some((column) => column.label === "Status")) {
      return activeDatasetConfig;
    }

    return {
      ...activeDatasetConfig,
      summary: {
        ...activeDatasetConfig.summary,
        columns: [
          ...summaryColumns,
          {
            label: "Status",
            sources: ["doc_status"],
            kind: "doc-status",
            className: "flex-[0.9] min-w-[7rem]",
            includeMissing: true,
            missingValue: DEFAULT_DOC_STATUS,
            formatValue: normalizeDocStatusValue,
          },
        ],
      },
    };
  }, [activeDatasetConfig, activeDataset, isAdminMode]);
  const {
    detailSourceRecord,
    isSearchMode,
    rowModels,
    selectedRows,
    detailModel,
  } = useDataViewDerived({
    records,
    selectedRowIds,
    activeConfig,
    activeDataset,
    selectedRecordId,
    detailRecord,
    submittedQuery,
  });
  const batchOptions = buildBulkDownloadBatchOptions(
    pagination.total,
    BULK_DOWNLOAD_BATCH_SIZE
  );
  const {
    handleBulkDownload,
    handleSingleDownload,
    handleSaveRecord,
    handleRevertRecord,
    handleMergeCompanies,
    handleDeleteCompanies,
    handleSearchCompanies,
    handleAddCompanySources,
    handleRemoveCompanySources,
  } =
    useDataViewActions({
      activeDataset,
      activeConfig,
      detailSourceRecord,
      selectedRecordId,
      mode,
      pagination,
      isAdminMode,
      applyRecordUpdate,
      setDownloadError,
      setIsBulkDownloading,
      setIsSavingRecord,
      setSaveRecordError,
      setIsRevertingRecord,
      setRevertRecordError,
      setCompanyBulkError,
      setIsMergingCompanies,
      setIsDeletingCompanies,
      refreshData,
      setSelectedRecordId,
      setIsDetailPanelOpen,
      setSelectedRowIds,
    });
  const {
    showPagination,
    bulkDownloadDisabled,
    primaryDownloadLabel,
    showRightDetailPanel,
    showTopDetailPanel,
    showMassDownloadControls,
    handleRowClick,
    handleCloseDetailPanel,
    handleBulkDownloadClick,
  } = useDataViewPageController({
    activeDataset,
    submittedQuery,
    isDetailPanelOpen,
    selectedRecordId,
    records,
    selectedRows,
    pagination,
    batchOptions,
    selectedBatchValue,
    isSearchMode,
    isBulkDownloading,
    isAdminMode,
    detailPanelPosition,
    detailPanelPositions: DETAIL_PANEL_POSITIONS,
    listLoading,
    setSelectedRecordId,
    setIsDetailPanelOpen,
    setSelectedBatchValue,
    setDownloadError,
    setSaveRecordError,
    setCompanyBulkError,
    setSelectedRowIds,
    handleBulkDownload,
    clearDetailState,
    beginDetailLoading,
  });

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#090d14] text-slate-100">
      {modeConfig.headerVariant === "admin" ? (
        <AdminHeader
          title={modeConfig.headerTitle}
          subtitle={modeConfig.headerSubtitle}
        />
      ) : (
        <Header />
      )}

      <main className="flex-1">
        <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
          <DataViewHeroSection
            activeConfig={activeConfig}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearchSubmit={submitSearch}
            onClearSearch={clearSearch}
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusChange}
            showDocStatusFilter={showDocStatusFilter}
            pagination={pagination}
            onPerPageChange={updatePerPage}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            listLoading={listLoading}
            showMassDownloadControls={showMassDownloadControls}
            modeConfig={modeConfig}
            selectedRows={selectedRows}
            isSearchMode={isSearchMode}
            batchOptions={batchOptions}
            selectedBatchValue={selectedBatchValue}
            onSelectedBatchChange={setSelectedBatchValue}
            isBulkDownloading={isBulkDownloading}
            bulkDownloadDisabled={bulkDownloadDisabled}
            primaryDownloadLabel={primaryDownloadLabel}
            onBulkDownload={handleBulkDownloadClick}
            downloadError={downloadError}
            batchSize={BULK_DOWNLOAD_BATCH_SIZE}
            showCompanyBulkActions={isAdminMode && activeDataset === "company" && selectedRows.length > 0}
            showCompanyAttachActions={isAdminMode && activeDataset !== "company" && selectedRows.length > 0}
            companyBulkSelectedRows={selectedRows}
            companyAttachSelectedRows={selectedRows}
            isMergingCompanies={isMergingCompanies}
            isDeletingCompanies={isDeletingCompanies}
            companyBulkError={companyBulkError}
            onMergeCompanies={({ primaryNameMode, primaryName }) =>
              handleMergeCompanies({
                selectedRows,
                primaryNameMode,
                primaryName,
              })
            }
            onDeleteCompanies={() =>
              handleDeleteCompanies({
                selectedRows,
              })
            }
            onSearchCompanies={handleSearchCompanies}
            onAttachCompany={({ selectedRows: attachedRows, companyRecord }) =>
              handleAddCompanySources({
                selectedRows: attachedRows,
                companyRecord,
              })
            }
            datasets={datasets}
            activeDataset={activeDataset}
            configByDataset={datasetViewConfig}
            onDatasetChange={updateDataset}
            detailPanelPosition={detailPanelPosition}
            detailPanelPositions={DETAIL_PANEL_POSITIONS}
            onDetailPanelPositionChange={setDetailPanelPosition}
          />

          <div className={showRightDetailPanel ? "grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.95fr)]" : "grid gap-6"}>
            <div className="min-w-0 space-y-6">
              <DatasetRecordTable
                totalRecords={pagination.total}
                totalIsEstimated={pagination.total_is_estimated}
                summaryConfig={activeConfig.summary}
                theme={activeConfig.theme}
                rows={rowModels}
                selectedRecordId={selectedRecordId}
                enableRowSelection
                selectedRowIds={selectedRowIds}
                onToggleRowSelection={toggleRow}
                onToggleSelectAllRows={toggleAll}
                onRowClick={handleRowClick}
                loading={listLoading}
                error={listError}
                query={submittedQuery}
              />
            </div>

            {showRightDetailPanel ? (
              <div className="min-w-0 xl:sticky xl:top-24 xl:self-start">
                <RecordDetailCard
                  datasetKey={activeDataset}
                  datasetLabel={activeConfig.label}
                  detailModel={detailModel}
                  displayRecord={detailSourceRecord}
                  editableRecord={editableDetailRecord}
                  auditMetadata={detailAuditMetadata}
                  theme={activeConfig.theme}
                  loading={detailLoading}
                  error={detailError}
                  onClose={handleCloseDetailPanel}
                  onDownload={handleSingleDownload}
                  onSave={handleSaveRecord}
                  canEdit={isAdminMode && !isViewingOriginalRecord}
                  isOriginalView={isViewingOriginalRecord}
                  onViewOriginal={viewOriginalRecord}
                  onViewCurrent={viewCurrentRecord}
                  onRevert={handleRevertRecord}
                  onAddCompanySources={handleAddCompanySources}
                  onRemoveCompanySources={handleRemoveCompanySources}
                  isSaving={isSavingRecord || isMergingCompanies}
                  isReverting={isRevertingRecord}
                  saveError={saveRecordError}
                  revertError={revertRecordError}
                  downloadDisabled={detailLoading || !detailSourceRecord}
                  downloadLabel="Download Record CSV"
                  onOpenLinkedRecord={openLinkedRecord}
                />
              </div>
            ) : null}
          </div>
        </div>
      </main>

      {showTopDetailPanel ? (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-slate-950/72 px-4 pb-6 pt-5 backdrop-blur-sm sm:px-6 lg:px-8"
          onClick={handleCloseDetailPanel}
          role="presentation"
        >
          <div
            className="max-h-[calc(100vh-7rem)] w-full max-w-[1120px] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex justify-end">
              <div className="rounded-full border border-slate-700/80 bg-slate-950/90 px-3 py-1 text-[11px] font-medium text-slate-400 shadow-[0_10px_30px_rgba(2,6,23,0.45)]">
                Press <span className="font-semibold text-slate-200">Esc</span> to close
              </div>
            </div>
            <RecordDetailCard
              datasetKey={activeDataset}
              datasetLabel={activeConfig.label}
              detailModel={detailModel}
              displayRecord={detailSourceRecord}
              editableRecord={editableDetailRecord}
              auditMetadata={detailAuditMetadata}
              theme={activeConfig.theme}
              loading={detailLoading}
              error={detailError}
              onClose={handleCloseDetailPanel}
              onDownload={handleSingleDownload}
              onSave={handleSaveRecord}
              canEdit={isAdminMode && !isViewingOriginalRecord}
              isOriginalView={isViewingOriginalRecord}
              onViewOriginal={viewOriginalRecord}
              onViewCurrent={viewCurrentRecord}
              onRevert={handleRevertRecord}
              onAddCompanySources={handleAddCompanySources}
              onRemoveCompanySources={handleRemoveCompanySources}
              isSaving={isSavingRecord || isMergingCompanies}
              isReverting={isRevertingRecord}
              saveError={saveRecordError}
              revertError={revertRecordError}
              downloadDisabled={detailLoading || !detailSourceRecord}
              downloadLabel="Download Record CSV"
              onOpenLinkedRecord={openLinkedRecord}
            />
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-4 px-4 pb-2 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-slate-700/30 bg-slate-800/30 p-4">
          {showPagination ? (
            <Pagination
              currentPage={pagination.page}
              setCurrentPage={goToPage}
              totalPages={pagination.total_pages}
              hasMoreRecords={pagination.has_more}
              isCursorPagination
              onLastPage={goToLastPage}
            />
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DataViewPage;
